import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, FileSpreadsheet, CheckCircle2, AlertCircle, ArrowRight,
  ArrowLeft, Users, Briefcase, Calendar, Folder, Settings, Download,
  RefreshCw, X, ChevronDown, ChevronRight, Loader2, AlertTriangle,
  FileText, SkipForward, Plus, Merge, Replace
} from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { toast } from 'sonner';

const MODULE_ICONS = {
  clients: Users,
  employees: Briefcase,
  documents: Folder,
  schedules: Calendar,
  agency_settings: Settings,
};

const DUPLICATE_ACTIONS = [
  { value: 'skip', label: 'Skip', icon: SkipForward, description: 'Do not import this row' },
  { value: 'create_new', label: 'Create New', icon: Plus, description: 'Import as new record' },
  { value: 'merge', label: 'Merge', icon: Merge, description: 'Fill empty fields in existing' },
  { value: 'update', label: 'Update', icon: Replace, description: 'Replace existing with new data' },
];

const WIZARD_STEPS = [
  { id: 1, title: 'Select Data Type', description: 'Choose what to import' },
  { id: 2, title: 'Upload File', description: 'Upload CSV or Excel' },
  { id: 3, title: 'Map Fields', description: 'Match columns to fields' },
  { id: 4, title: 'Review & Validate', description: 'Check for errors' },
  { id: 5, title: 'Import', description: 'Execute import' },
];

export default function DataMigrationWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [template, setTemplate] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [importJob, setImportJob] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [duplicateDecisions, setDuplicateDecisions] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedDuplicates, setExpandedDuplicates] = useState({});

  // Load modules on mount
  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    try {
      const response = await api.get('/migration/modules');
      setModules(response.data.modules);
    } catch (error) {
      toast.error('Failed to load import modules');
      console.error(error);
    }
  };

  const loadTemplate = async (moduleId) => {
    try {
      setLoading(true);
      const response = await api.get(`/migration/templates/${moduleId}`);
      setTemplate(response.data);
    } catch (error) {
      toast.error('Failed to load template');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleModuleSelect = async (moduleId) => {
    setSelectedModule(moduleId);
    await loadTemplate(moduleId);
  };

  const handleFileUpload = async (file) => {
    if (!selectedModule) {
      toast.error('Please select a data type first');
      return;
    }

    const allowedTypes = ['.csv', '.xlsx', '.xls', '.json'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(ext)) {
      toast.error('Invalid file type. Please use CSV, Excel (.xlsx), or JSON files.');
      return;
    }

    setUploadedFile(file);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/migration/upload/${selectedModule}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        setImportJob(response.data);
        toast.success(`File uploaded: ${response.data.total_rows} rows detected`);
        setCurrentStep(3);
      } else {
        toast.error(response.data.errors?.[0] || 'Upload failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!importJob?.job_id) return;

    setLoading(true);
    try {
      const response = await api.post(`/migration/jobs/${importJob.job_id}/validate`);
      setValidationResult(response.data);
      
      // Initialize duplicate decisions to 'skip' by default
      if (response.data.duplicates?.length > 0) {
        const decisions = {};
        response.data.duplicates.forEach(dup => {
          decisions[dup.row_number] = 'skip';
        });
        setDuplicateDecisions(decisions);
      }

      if (response.data.validation_passed) {
        toast.success('Validation passed!');
      } else {
        toast.warning(`Found ${response.data.error_count} errors and ${response.data.warning_count} warnings`);
      }
      setCurrentStep(4);
    } catch (error) {
      toast.error('Validation failed');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateDecision = (rowNumber, action) => {
    setDuplicateDecisions(prev => ({
      ...prev,
      [rowNumber]: action
    }));
  };

  const handleSetAllDuplicates = (action) => {
    if (!validationResult?.duplicates) return;
    const decisions = {};
    validationResult.duplicates.forEach(dup => {
      decisions[dup.row_number] = action;
    });
    setDuplicateDecisions(decisions);
    toast.success(`Set all duplicates to: ${DUPLICATE_ACTIONS.find(a => a.value === action)?.label}`);
  };

  const handleExecuteImport = async () => {
    if (!importJob?.job_id) return;

    setImporting(true);
    try {
      // Convert duplicate decisions to the format expected by the backend
      const decisionsPayload = {};
      Object.entries(duplicateDecisions).forEach(([rowNum, action]) => {
        decisionsPayload[parseInt(rowNum)] = action;
      });

      const response = await api.post(`/migration/jobs/${importJob.job_id}/execute`, {
        duplicate_decisions: Object.keys(decisionsPayload).length > 0 ? decisionsPayload : null
      });

      setImportResult(response.data);
      setCurrentStep(5);

      if (response.data.success) {
        toast.success(`Successfully imported ${response.data.imported_count} records!`);
      } else {
        toast.warning(`Import completed with ${response.data.error_count} errors`);
      }
    } catch (error) {
      toast.error('Import failed');
      console.error(error);
    } finally {
      setImporting(false);
    }
  };

  const handleRollback = async () => {
    if (!importJob?.job_id) return;

    if (!window.confirm('Are you sure you want to rollback this import? All imported records will be deleted.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`/migration/jobs/${importJob.job_id}/rollback`);
      toast.success(`Rolled back ${response.data.deleted_count} records`);
      // Reset wizard
      setCurrentStep(1);
      setImportJob(null);
      setValidationResult(null);
      setImportResult(null);
    } catch (error) {
      toast.error('Rollback failed');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!selectedModule) return;
    
    try {
      const response = await api.get(`/migration/templates/${selectedModule}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedModule}_import_template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const resetWizard = () => {
    setCurrentStep(1);
    setSelectedModule(null);
    setTemplate(null);
    setUploadedFile(null);
    setImportJob(null);
    setValidationResult(null);
    setDuplicateDecisions({});
    setImportResult(null);
  };

  return (
    <div className="px-4 md:px-8 py-6 md:py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Data Migration Wizard</h1>
          <p className="text-muted-foreground mt-1">
            Import your existing data into Leynix
          </p>
        </div>
        {currentStep > 1 && currentStep < 5 && (
          <Button variant="outline" onClick={resetWizard}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Start Over
          </Button>
        )}
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between max-w-3xl mx-auto">
        {WIZARD_STEPS.map((step, idx) => (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  currentStep > step.id
                    ? 'bg-primary text-primary-foreground'
                    : currentStep === step.id
                    ? 'bg-primary text-primary-foreground ring-4 ring-primary/20'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {currentStep > step.id ? <CheckCircle2 className="w-5 h-5" /> : step.id}
              </div>
              <span className={`text-xs mt-2 hidden md:block ${currentStep >= step.id ? 'text-foreground' : 'text-muted-foreground'}`}>
                {step.title}
              </span>
            </div>
            {idx < WIZARD_STEPS.length - 1 && (
              <div className={`flex-1 h-1 mx-2 rounded ${currentStep > step.id ? 'bg-primary' : 'bg-muted'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {/* Step 1: Select Module */}
          {currentStep === 1 && (
            <div className="max-w-4xl mx-auto">
              <Card>
                <CardHeader>
                  <CardTitle>What would you like to import?</CardTitle>
                  <CardDescription>
                    Select the type of data you want to migrate into Leynix
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {modules.map((module) => {
                      const Icon = MODULE_ICONS[module.id] || FileText;
                      const isSelected = selectedModule === module.id;
                      const isDisabled = module.id === 'documents' || module.id === 'agency_settings';
                      
                      return (
                        <button
                          key={module.id}
                          onClick={() => !isDisabled && handleModuleSelect(module.id)}
                          disabled={isDisabled}
                          className={`p-4 rounded-lg border-2 text-left transition-all ${
                            isDisabled
                              ? 'opacity-50 cursor-not-allowed border-muted'
                              : isSelected
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50 hover:bg-accent/50'
                          }`}
                          data-testid={`module-${module.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary/10 text-primary' : 'bg-muted'}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium">{module.name}</h3>
                                {isDisabled && <Badge variant="secondary">Coming Soon</Badge>}
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">
                                {module.description}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {selectedModule && template && (
                    <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">Template Preview</h4>
                        <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                          <Download className="w-4 h-4 mr-2" />
                          Download Template
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        Required columns: <span className="font-medium">{template.columns.slice(0, 5).join(', ')}</span>
                        {template.columns.length > 5 && ` +${template.columns.length - 5} more`}
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        {template.accepted_formats.map(fmt => (
                          <Badge key={fmt} variant="outline">.{fmt}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end mt-6">
                    <Button
                      onClick={() => setCurrentStep(2)}
                      disabled={!selectedModule}
                      data-testid="next-step-1"
                    >
                      Continue
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 2: Upload File */}
          {currentStep === 2 && (
            <div className="max-w-2xl mx-auto">
              <Card>
                <CardHeader>
                  <CardTitle>Upload Your File</CardTitle>
                  <CardDescription>
                    Upload a CSV, Excel (.xlsx), or JSON file containing your {selectedModule} data
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div
                    className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                    onClick={() => document.getElementById('file-upload').click()}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('border-primary'); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-primary');
                      const file = e.dataTransfer.files[0];
                      if (file) handleFileUpload(file);
                    }}
                  >
                    <input
                      id="file-upload"
                      type="file"
                      className="hidden"
                      accept=".csv,.xlsx,.xls,.json"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file);
                      }}
                    />
                    {loading ? (
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 className="w-12 h-12 text-primary animate-spin" />
                        <p className="text-muted-foreground">Processing file...</p>
                      </div>
                    ) : (
                      <>
                        <FileSpreadsheet className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-lg font-medium mb-2">
                          Drop your file here or click to browse
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Supports CSV, Excel (.xlsx), and JSON files up to 10MB
                        </p>
                      </>
                    )}
                  </div>

                  {template?.tips && (
                    <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-2">Tips for Success</h4>
                      <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-1">
                        {template.tips.map((tip, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex justify-between mt-6">
                    <Button variant="outline" onClick={() => setCurrentStep(1)}>
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 3: Map Fields */}
          {currentStep === 3 && importJob && (
            <div className="max-w-4xl mx-auto">
              <Card>
                <CardHeader>
                  <CardTitle>Review Column Mapping</CardTitle>
                  <CardDescription>
                    We detected {importJob.total_rows} rows. Verify the column mappings below.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-700 dark:text-green-300">
                          {importJob.detected_columns?.length || 0} columns detected
                        </p>
                        <p className="text-sm text-green-600 dark:text-green-400">
                          {importJob.detected_columns?.slice(0, 5).join(', ')}
                          {importJob.detected_columns?.length > 5 && '...'}
                        </p>
                      </div>
                    </div>

                    {/* Sample Data Preview */}
                    {importJob.sample_data?.length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-muted px-4 py-2 font-medium">
                          Preview (First {importJob.sample_data.length} rows)
                        </div>
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-12">#</TableHead>
                                {importJob.detected_columns?.slice(0, 6).map((col) => (
                                  <TableHead key={col}>{col}</TableHead>
                                ))}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {importJob.sample_data.slice(0, 3).map((row, idx) => (
                                <TableRow key={idx}>
                                  <TableCell className="font-medium">{idx + 1}</TableCell>
                                  {importJob.detected_columns?.slice(0, 6).map((col) => (
                                    <TableCell key={col} className="max-w-[200px] truncate">
                                      {row[col] || '-'}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between mt-6">
                    <Button variant="outline" onClick={() => setCurrentStep(2)}>
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    <Button onClick={handleValidate} disabled={loading} data-testid="validate-btn">
                      {loading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                      )}
                      Validate Data
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 4: Review & Validate */}
          {currentStep === 4 && validationResult && (
            <div className="max-w-5xl mx-auto space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">{validationResult.total_rows}</div>
                    <div className="text-sm text-muted-foreground">Total Rows</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-green-600">{validationResult.valid_rows}</div>
                    <div className="text-sm text-muted-foreground">Valid Rows</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-red-600">{validationResult.error_count}</div>
                    <div className="text-sm text-muted-foreground">Errors</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-yellow-600">{validationResult.warning_count}</div>
                    <div className="text-sm text-muted-foreground">Warnings</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-blue-600">{validationResult.duplicate_count}</div>
                    <div className="text-sm text-muted-foreground">Duplicates</div>
                  </CardContent>
                </Card>
              </div>

              {/* Validation Status */}
              <Card>
                <CardHeader className={validationResult.validation_passed ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}>
                  <div className="flex items-center gap-3">
                    {validationResult.validation_passed ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-red-600" />
                    )}
                    <div>
                      <CardTitle className={validationResult.validation_passed ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}>
                        {validationResult.validation_passed ? 'Validation Passed' : 'Validation Issues Found'}
                      </CardTitle>
                      <CardDescription>
                        {validationResult.validation_passed
                          ? 'Your data is ready to import. Review any warnings below.'
                          : `Please fix ${validationResult.error_count} error(s) before importing.`}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* Duplicates Section */}
              {validationResult.duplicates?.length > 0 && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-yellow-600" />
                          Duplicate Records Found ({validationResult.duplicates.length})
                        </CardTitle>
                        <CardDescription>
                          Choose how to handle each duplicate. Every decision is logged in the audit trail.
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        {DUPLICATE_ACTIONS.map((action) => (
                          <Button
                            key={action.value}
                            variant="outline"
                            size="sm"
                            onClick={() => handleSetAllDuplicates(action.value)}
                          >
                            {action.label} All
                          </Button>
                        ))}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {validationResult.duplicates.map((dup) => (
                        <Collapsible
                          key={dup.row_number}
                          open={expandedDuplicates[dup.row_number]}
                          onOpenChange={(open) => setExpandedDuplicates(prev => ({ ...prev, [dup.row_number]: open }))}
                        >
                          <div className="border rounded-lg">
                            <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-accent/50">
                              <div className="flex items-center gap-4">
                                <Badge variant="outline">Row {dup.row_number}</Badge>
                                <span className="font-medium">
                                  {dup.source_data?.first_name} {dup.source_data?.last_name}
                                  {dup.source_data?.email && ` (${dup.source_data.email})`}
                                </span>
                                <Badge variant="secondary">
                                  {Math.round(dup.match_confidence * 100)}% match on {dup.match_fields?.join(', ')}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-3">
                                <Select
                                  value={duplicateDecisions[dup.row_number] || 'skip'}
                                  onValueChange={(value) => handleDuplicateDecision(dup.row_number, value)}
                                >
                                  <SelectTrigger className="w-[140px]" onClick={(e) => e.stopPropagation()}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {DUPLICATE_ACTIONS.map((action) => (
                                      <SelectItem key={action.value} value={action.value}>
                                        <span className="flex items-center gap-2">
                                          <action.icon className="w-4 h-4" />
                                          {action.label}
                                        </span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {expandedDuplicates[dup.row_number] ? (
                                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                                )}
                              </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <div className="p-4 pt-0 grid grid-cols-2 gap-4">
                                <div>
                                  <h5 className="font-medium mb-2 text-blue-600">New Record (from file)</h5>
                                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-sm space-y-1">
                                    {Object.entries(dup.source_data || {}).filter(([k]) => !k.startsWith('_')).slice(0, 8).map(([key, value]) => (
                                      <div key={key} className="flex justify-between">
                                        <span className="text-muted-foreground">{key}:</span>
                                        <span className="font-medium">{value || '-'}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <h5 className="font-medium mb-2 text-green-600">Existing Record (in Leynix)</h5>
                                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-sm space-y-1">
                                    {Object.entries(dup.existing_record || {}).filter(([k]) => !k.startsWith('_')).slice(0, 8).map(([key, value]) => (
                                      <div key={key} className="flex justify-between">
                                        <span className="text-muted-foreground">{key}:</span>
                                        <span className="font-medium">{String(value) || '-'}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </CollapsibleContent>
                          </div>
                        </Collapsible>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Issues List */}
              {validationResult.issues?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Validation Issues</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {validationResult.issues.filter(i => i.field !== 'record').map((issue, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg flex items-start gap-3 ${
                            issue.severity === 'error'
                              ? 'bg-red-50 dark:bg-red-900/20'
                              : 'bg-yellow-50 dark:bg-yellow-900/20'
                          }`}
                        >
                          {issue.severity === 'error' ? (
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                          ) : (
                            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                          )}
                          <div>
                            <div className="font-medium">
                              Row {issue.row_number}: {issue.field}
                            </div>
                            <div className="text-sm text-muted-foreground">{issue.message}</div>
                            {issue.suggested_fix && (
                              <div className="text-sm text-blue-600 mt-1">Suggestion: {issue.suggested_fix}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(3)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleExecuteImport}
                  disabled={!validationResult.validation_passed || importing}
                  data-testid="execute-import-btn"
                >
                  {importing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {importing ? 'Importing...' : 'Execute Import'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 5: Import Complete */}
          {currentStep === 5 && importResult && (
            <div className="max-w-2xl mx-auto">
              <Card>
                <CardHeader className={importResult.success ? 'bg-green-50 dark:bg-green-900/20' : 'bg-yellow-50 dark:bg-yellow-900/20'}>
                  <div className="flex items-center gap-3">
                    {importResult.success ? (
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-8 h-8 text-yellow-600" />
                    )}
                    <div>
                      <CardTitle className={importResult.success ? 'text-green-700' : 'text-yellow-700'}>
                        {importResult.success ? 'Import Successful!' : 'Import Completed with Warnings'}
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <div className="text-3xl font-bold text-green-600">{importResult.imported_count}</div>
                      <div className="text-sm text-muted-foreground">Records Imported</div>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-3xl font-bold">{importResult.skipped_count}</div>
                      <div className="text-sm text-muted-foreground">Skipped</div>
                    </div>
                    {importResult.updated_count > 0 && (
                      <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="text-3xl font-bold text-blue-600">{importResult.updated_count}</div>
                        <div className="text-sm text-muted-foreground">Updated</div>
                      </div>
                    )}
                    {importResult.merged_count > 0 && (
                      <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <div className="text-3xl font-bold text-purple-600">{importResult.merged_count}</div>
                        <div className="text-sm text-muted-foreground">Merged</div>
                      </div>
                    )}
                    {importResult.error_count > 0 && (
                      <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <div className="text-3xl font-bold text-red-600">{importResult.error_count}</div>
                        <div className="text-sm text-muted-foreground">Errors</div>
                      </div>
                    )}
                  </div>

                  {importResult.warnings?.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-medium mb-2">Import Notes</h4>
                      <div className="bg-muted rounded-lg p-3 max-h-40 overflow-y-auto text-sm space-y-1">
                        {importResult.warnings.map((warning, idx) => (
                          <div key={idx}>{warning}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {importResult.errors?.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-medium mb-2 text-red-600">Errors</h4>
                      <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 max-h-40 overflow-y-auto text-sm space-y-1">
                        {importResult.errors.map((error, idx) => (
                          <div key={idx} className="text-red-600">{error}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button variant="outline" onClick={handleRollback} className="flex-1">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Rollback Import
                    </Button>
                    <Button onClick={resetWizard} className="flex-1">
                      Import More Data
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => navigate(selectedModule === 'clients' ? '/clients' : '/settings')}
                      className="flex-1"
                    >
                      View {selectedModule === 'clients' ? 'Clients' : selectedModule === 'employees' ? 'Employees' : 'Data'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
